import { Button, EmptyState, Grid, GridRow, Modal, Select, SelectOptionType } from '@bp/ui-components';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isPlaced } from '../../../stores/TimetableVersion/SubjectContainerTypes';
import { AvailabilityResourceType } from '../../../stores/TimetableVersion/TimeAvailabilityStore';
import {
  availabilityResourceTypeToWarningType,
  getWarningTypeTranslation,
} from '../../../utils/getWarningTypeTranslation';
import { useTimetableStore } from '../TimetableProvider';
import styles from './ConflictListModal.module.scss';

type TypeFilter = AvailabilityResourceType | 'all';

type ResourceConflict = {
  warningLabel: string;
  resourceName: string;
  conflictType: AvailabilityResourceType;
  epochDateRanges: string[];
};

type ConflictByOtherCard = {
  otherCardUuid: string | null;
  otherCardLabel: string;
  resources: ResourceConflict[];
};

type CardConflictEntry = {
  cardUuid: string;
  cardLabel: string;
  timeLabel: string;
  timeOrder: number;
  dayValue: string;
  dayLabel: string;
  classUuids: string[];
  conflictTypes: Set<AvailabilityResourceType>;
  conflictsByOtherCard: ConflictByOtherCard[];
};

export const ConflictListModal: FC = observer(() => {
  const { t } = useTranslation();
  const store = useTimetableStore();
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [classFilter, setClassFilter] = useState<string | null>(null);

  const allCardEntries: CardConflictEntry[] = [];

  store.cardConflicts.forEach((cardAvailability, cardUuid) => {
    const card = store.getCardByUuid(cardUuid);
    if (!card) return;

    const subjectName = card.subject?.shortName ?? '';
    const classNames = card.classes.map((c) => c.shortName).join(', ');
    const cardLabel = [subjectName, classNames].filter(Boolean).join(' · ');

    const day = store.days.find((d) => d.value === card.weekday);
    const timeEntries = store.placedCardsTimeGridEntries.get(cardUuid) ?? [];
    const startHourIndex = store.hours.findIndex((h) => h.value === timeEntries[0]);
    const endHourIndex = store.hours.findIndex((h) => h.value === timeEntries[timeEntries.length - 1]);
    const startHour = startHourIndex >= 0 ? store.hours[startHourIndex] : undefined;
    const endHour = endHourIndex >= 0 ? store.hours[endHourIndex] : undefined;
    const timeLabel =
      startHour && endHour && startHour.value !== endHour.value
        ? `${startHour.label} – ${endHour.label}`
        : (startHour?.label ?? '');

    const conflictTypes = new Set<AvailabilityResourceType>();
    const byOtherCard = new Map<string, ConflictByOtherCard>();

    const fmt = (d: string) => dayjs(d).format('DD.MM.');
    const getEpochRanges = (resourceUuid: string, type: AvailabilityResourceType, placedUuids: string[]): string[] => {
      if (type !== 'teacher' && type !== 'room') return [];
      const ranges: string[] = [];
      placedUuids.forEach((uid) => {
        store.subjectContainerSlots
          .get(uid)
          ?.filter(isPlaced)
          .filter((s) => (type === 'teacher' ? s.teacherUuids : s.roomUuids).includes(resourceUuid))
          .forEach((s) => ranges.push(`${fmt(s.startDate)} – ${fmt(s.endDate)}`));
      });
      if (ranges.length === 0) {
        store.subjectContainerSlots
          .get(cardUuid)
          ?.filter(isPlaced)
          .filter((s) => (type === 'teacher' ? s.teacherUuids : s.roomUuids).includes(resourceUuid))
          .forEach((s) => ranges.push(`${fmt(s.startDate)} – ${fmt(s.endDate)}`));
      }
      return ranges;
    };

    const buildOtherCardLabel = (otherUuid: string): string => {
      const other = store.getCardByUuid(otherUuid);
      if (!other) return otherUuid;
      return [other.subject?.name, other.classes.map((c) => c.name).join(', ')].filter(Boolean).join(' · ');
    };

    cardAvailability.forEach((conflict, resourceUuid) => {
      const placedWithoutCurrent = Array.from(conflict.placed).filter((u) => u !== cardUuid);
      if (!conflict.blocked && !conflict.maybe && placedWithoutCurrent.length === 0) return;

      const warningType = availabilityResourceTypeToWarningType(conflict.type, conflict.blocked, conflict.maybe);
      const resourceName = store.getNameForUuid(resourceUuid, conflict.type);
      const epochDateRanges = getEpochRanges(resourceUuid, conflict.type, placedWithoutCurrent);

      conflictTypes.add(conflict.type);

      const resource: ResourceConflict = {
        warningLabel: getWarningTypeTranslation(warningType),
        resourceName,
        conflictType: conflict.type,
        epochDateRanges,
      };

      if (conflict.blocked || conflict.maybe || placedWithoutCurrent.length === 0) {
        const key = '__unavailable__';
        if (!byOtherCard.has(key)) {
          byOtherCard.set(key, {
            otherCardUuid: null,
            otherCardLabel: t('pinboard.conflictList.unavailable', 'Nicht verfügbar'),
            resources: [],
          });
        }
        byOtherCard.get(key)!.resources.push(resource);
      } else {
        placedWithoutCurrent.forEach((otherUuid) => {
          if (!byOtherCard.has(otherUuid)) {
            byOtherCard.set(otherUuid, {
              otherCardUuid: otherUuid,
              otherCardLabel: buildOtherCardLabel(otherUuid),
              resources: [],
            });
          }
          byOtherCard.get(otherUuid)!.resources.push(resource);
        });
      }
    });

    if (byOtherCard.size === 0) return;

    allCardEntries.push({
      cardUuid,
      cardLabel,
      timeLabel,
      timeOrder: startHourIndex,
      dayValue: day?.value ?? '',
      dayLabel: day?.label ?? '',
      classUuids: card.classes.map((c) => c.uuid),
      conflictTypes,
      conflictsByOtherCard: Array.from(byOtherCard.values()),
    });
  });

  // Class filter options
  const classOptions: SelectOptionType[] = [];
  const seenClassUuids = new Set<string>();
  allCardEntries.forEach((entry) => {
    entry.classUuids.forEach((uuid) => {
      if (!seenClassUuids.has(uuid)) {
        seenClassUuids.add(uuid);
        const card = store.getCardByUuid(entry.cardUuid);
        const cls = card?.classes.find((c) => c.uuid === uuid);
        if (cls) classOptions.push({ value: cls.uuid, label: cls.shortName });
      }
    });
  });
  classOptions.sort((a, b) => (a.label ?? '').localeCompare(b.label ?? '', undefined, { numeric: true }));

  const countForType = (type: TypeFilter) =>
    type === 'all'
      ? allCardEntries.length
      : allCardEntries.filter((e) => e.conflictTypes.has(type as AvailabilityResourceType)).length;

  // Apply filters
  const filtered = allCardEntries
    .filter((entry) => !classFilter || entry.classUuids.includes(classFilter))
    .map((entry) => ({
      ...entry,
      conflictsByOtherCard: entry.conflictsByOtherCard
        .map((group) => ({
          ...group,
          resources:
            typeFilter === 'all' ? group.resources : group.resources.filter((r) => r.conflictType === typeFilter),
        }))
        .filter((group) => group.resources.length > 0),
    }))
    .filter((entry) => entry.conflictsByOtherCard.length > 0);

  const dayOrder = store.days.map((d) => d.value);
  const groupedByDay = dayOrder
    .map((dayValue) => ({
      dayValue,
      dayLabel: store.days.find((d) => d.value === dayValue)?.label ?? '',
      entries: filtered.filter((e) => e.dayValue === dayValue).sort((a, b) => a.timeOrder - b.timeOrder),
    }))
    .filter((group) => group.entries.length > 0);

  const typeFilterOptions: { type: TypeFilter; label: string }[] = [
    { type: 'all', label: t('pinboard.conflictList.filterAll') },
    { type: 'teacher', label: t('pinboard.conflictList.filterTeacher') },
    { type: 'class', label: t('pinboard.conflictList.filterClass') },
    { type: 'room', label: t('pinboard.conflictList.filterRoom') },
    { type: 'divisionGroup', label: t('pinboard.conflictList.filterDivision') },
  ];

  return (
    <Modal
      className={styles['conflict-list-modal']}
      isOpen={store.conflictListModal.isOpen}
      onRequestClose={() => store.setConflictListModalOpen(false)}
      title={t('pinboard.conflictList.title')}
      parent={document.fullscreenElement ? document.getElementById('timetable-grid') : null}
    >
      <Grid>
        <GridRow spacingBottom='s'>
          <div className={styles.filters}>
            {typeFilterOptions.map(({ type, label }) => {
              const count = countForType(type);
              if (type !== 'all' && count === 0) return null;
              return (
                <Button
                  key={type}
                  hierarchy={typeFilter === type ? 'primary' : 'secondary'}
                  onClick={() => setTypeFilter(type)}
                >
                  {label}
                </Button>
              );
            })}
          </div>
        </GridRow>
        {classOptions.length > 1 && (
          <GridRow spacingBottom='m'>
            <Select
              label={t('pinboard.conflictList.classFilterLabel')}
              name='class-filter'
              options={[{ value: null, label: t('pinboard.conflictList.allClasses') }, ...classOptions]}
              value={
                classFilter
                  ? (classOptions.find((o) => o.value === classFilter) ?? null)
                  : { value: null, label: t('pinboard.conflictList.allClasses') }
              }
              onChange={(option) => setClassFilter((option as SelectOptionType)?.value as string | null)}
              isClearable={false}
              isSearchable={classOptions.length > 6}
            />
          </GridRow>
        )}
        <GridRow direction='column'>
          {filtered.length === 0 ? (
            <EmptyState subtitle={t('pinboard.conflictList.noConflicts')} />
          ) : (
            <div className={styles.list}>
              {groupedByDay.map((group) => (
                <div key={group.dayValue}>
                  <div className={styles['day-group-header']}>{group.dayLabel}</div>
                  {group.entries.map((entry) => (
                    <div key={entry.cardUuid} className={styles.item}>
                      <div className={styles['card-label']}>
                        {entry.cardLabel}
                        {entry.timeLabel && <span className={styles['time-label']}>{entry.timeLabel}</span>}
                      </div>
                      <div className={styles['conflict-entries']}>
                        {entry.conflictsByOtherCard.map((otherGroup) => (
                          <Fragment key={otherGroup.otherCardUuid ?? '__unavailable__'}>
                            <div className={styles['other-card-label']}>{otherGroup.otherCardLabel}</div>
                            {otherGroup.resources.map((resource, i) => (
                              <div key={i} className={styles['conflict-row']}>
                                <span>{resource.warningLabel}</span>
                                {resource.resourceName && (
                                  <span className={styles['resource-name']}>{resource.resourceName}</span>
                                )}
                                {resource.epochDateRanges.length > 0 && (
                                  <div className={styles['epoch-range']}>{resource.epochDateRanges.join(', ')}</div>
                                )}
                              </div>
                            ))}
                          </Fragment>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </GridRow>
      </Grid>
    </Modal>
  );
});
